import React from 'react';
import type { MockOrder } from '../types';

export interface TimelineTabProps {
  order: MockOrder;
}

interface TimelineEvent {
  id: string;
  /** Hero-relative actor: 'system', shipper / broker / carrier / driver org names. */
  actor: string;
  /** One-liner description shown in bold above the timestamp. */
  title: string;
  /** Optional sub-line (free text, e.g. price snapshot, location, contract leg). */
  detail?: string;
  /** Wall-clock timestamp. */
  at: Date;
  /** When set, the dot uses an alternate accent color (used for completion). */
  emphasis?: 'created' | 'searching' | 'assigned' | 'in_transit' | 'delivered' | 'contract';
}

const dotClass: Record<NonNullable<TimelineEvent['emphasis']>, string> = {
  created: 'bg-gray-400 ring-gray-200',
  searching: 'bg-blue-500 ring-blue-200',
  assigned: 'bg-indigo-500 ring-indigo-200',
  in_transit: 'bg-amber-500 ring-amber-200',
  delivered: 'bg-emerald-500 ring-emerald-200',
  contract: 'bg-purple-500 ring-purple-200',
};

const formatExact = (d: Date) =>
  d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatRelative = (d: Date) => {
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const HOUR = 60 * 60_000;

/**
 * Synthesize a chain-aware timeline from a `MockOrder`. In production
 * this maps directly to `Order.timeline[]` plus `contracts[]` events
 * filtered by `useOrderViewerLens` — the structure here mirrors that
 * shape so the migration is a one-liner replacement.
 */
const buildEvents = (order: MockOrder): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  const t0 = order.createdAt.getTime();

  events.push({
    id: 'created',
    actor: order.shipper.name,
    title: 'Order created',
    detail: `${order.pickup.city} → ${order.dropoff.city}`,
    at: order.createdAt,
    emphasis: 'created',
  });

  if (
    order.status === 'searching' ||
    order.status === 'assigned' ||
    order.status === 'in_transit' ||
    order.status === 'delivered'
  ) {
    events.push({
      id: 'searching',
      actor: 'system',
      title: 'Searching for capacity',
      detail: `Mode: ${order.pricingMode.replace('_', ' ')}`,
      at: new Date(t0 + 0.25 * HOUR),
      emphasis: 'searching',
    });
  }

  // One contract event per leg (chain-aware).
  order.legs.forEach((leg, idx) => {
    events.push({
      id: `contract-${idx}`,
      actor: leg.buyer,
      title: `Contract leg ${idx + 1} accepted`,
      detail: `${leg.buyer} → ${leg.seller} · ${leg.amount.toLocaleString()} ${leg.currency} (${leg.pricingSource})`,
      at: new Date(t0 + (0.5 + idx * 0.1) * HOUR),
      emphasis: 'contract',
    });
  });

  if (
    order.driver &&
    (order.status === 'assigned' ||
      order.status === 'in_transit' ||
      order.status === 'delivered')
  ) {
    events.push({
      id: 'driver-assigned',
      actor: order.carrier?.name ?? 'Carrier',
      title: 'Driver assigned',
      detail: `${order.driver.name} · ${order.driver.phone}`,
      at: new Date(t0 + 1.5 * HOUR),
      emphasis: 'assigned',
    });
  }

  if (order.status === 'in_transit' || order.status === 'delivered') {
    events.push({
      id: 'pickup',
      actor: order.driver?.name ?? 'Driver',
      title: 'Picked up at origin',
      detail: order.pickup.city,
      at: new Date(t0 + 3 * HOUR),
      emphasis: 'in_transit',
    });
  }

  if (order.status === 'delivered') {
    events.push({
      id: 'delivered',
      actor: order.driver?.name ?? 'Driver',
      title: 'Delivered',
      detail: `Signed at ${order.dropoff.city}`,
      at: new Date(t0 + 8 * HOUR),
      emphasis: 'delivered',
    });
  }

  return events.sort((a, b) => a.at.getTime() - b.at.getTime());
};

/**
 * Vertical timeline. Each entry is a left-edge dot + connecting line
 * + a content block (title, detail, actor + relative timestamp).
 */
export const TimelineTab: React.FC<TimelineTabProps> = ({ order }) => {
  const events = buildEvents(order);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <ol className="relative">
        {events.map((evt, idx) => {
          const last = idx === events.length - 1;
          return (
            <li key={evt.id} className="flex gap-4 pb-5 last:pb-0">
              <div className="relative flex w-3 shrink-0 flex-col items-center">
                <span
                  className={`mt-1 inline-block size-3 rounded-full ring-4 ${dotClass[evt.emphasis ?? 'created']}`}
                />
                {!last && (
                  <span className="absolute top-4 bottom-[-1.25rem] w-px bg-gray-200" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-gray-900">
                  {evt.title}
                </p>
                {evt.detail && (
                  <p className="mt-0.5 text-[12px] text-gray-600">{evt.detail}</p>
                )}
                <p className="mt-1 text-[11px] text-gray-400">
                  {evt.actor === 'system' ? 'System' : evt.actor}
                  <span className="mx-1.5 text-gray-300">·</span>
                  <time title={formatExact(evt.at)}>{formatRelative(evt.at)}</time>
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
